import { useState } from 'react';
import { useSoftphone } from '../telephony/SoftphoneContext';
import { useNow, useResource } from '../state/hooks';
import { duration, elapsed, phone, since, titleCase } from '../lib/format';
import type { DirectoryUser } from '../lib/types';
import { Banner, Card, Field, Modal } from './ui';

/**
 * The agent's handset.
 *
 * Its job is to keep three things unambiguous at a glance: whether the line is
 * registered, who is on it, and how long they have been there. Everything else
 * is one click away.
 */
export function Softphone() {
  const phoneState = useSoftphone();
  const now = useNow();
  const [dialing, setDialing] = useState('');
  const [showPad, setShowPad] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!phoneState.available) return null;

  const call = phoneState.activeCall;
  const held = call?.state === 'on_hold';
  const talkSeconds = call ? since(call.answered_at ?? call.created_at, now) : 0;

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const registrationTone =
    phoneState.registration === 'registered' ? 'ok'
      : phoneState.registration === 'failed' ? 'danger'
        : 'warn';

  return (
    <Card
      title="Softphone"
      actions={
        <span className={`badge ${registrationTone}`}>
          <i className="dot" />
          {titleCase(phoneState.registration)}
        </span>
      }
    >
      <div className="softphone">
        {phoneState.registrationDetail && (
          <div className="phone-state">
            <span className="muted">{phoneState.registrationDetail}</span>
          </div>
        )}
        {phoneState.driver === 'mock' && (
          <div className="faint">
            Simulated trunk: call control is real, audio is not carried.
          </div>
        )}
        {(error || phoneState.error) && <Banner kind="error">{error ?? phoneState.error}</Banner>}

        {call ? (
          <div className={`live-call${held ? ' held' : ''}`}>
            <div className="row between">
              <div>
                <div className="number">{phone(call.direction === 'inbound' ? call.ani : call.dnis)}</div>
                <div className="faint">
                  {call.known_caller_name ?? call.caller_id_name ?? 'Unknown caller'}
                  {call.queue_name ? ` · ${call.queue_name}` : ''}
                  {call.incident_no ? ` · ${call.incident_no}` : ''}
                </div>
              </div>
              <div className="stack tight" style={{ alignItems: 'flex-end' }}>
                <div className="timer">{duration(talkSeconds)}</div>
                {phoneState.recording && (
                  <span className="row tight faint">
                    <i className="rec-dot" /> recording
                  </span>
                )}
              </div>
            </div>

            {call.known_caller_address && (
              <div className="faint" style={{ marginTop: 6 }}>
                Known address: {call.known_caller_address}
              </div>
            )}
            {call.known_caller_notes && (
              <Banner kind="warn">{call.known_caller_notes}</Banner>
            )}

            <div className="row" style={{ marginTop: 10 }}>
              <button className="btn danger" disabled={busy} onClick={() => void run(phoneState.hangup)}>
                Hang up
              </button>
              <button className="btn" disabled={busy} onClick={() => void run(() => phoneState.setHold(!held))}>
                {held ? 'Resume' : 'Hold'}
              </button>
              <button className="btn" disabled={busy} onClick={() => void run(phoneState.toggleMute)}>
                {phoneState.muted ? 'Unmute' : 'Mute'}
              </button>
              <button className="btn" disabled={busy} onClick={() => setShowTransfer(true)}>
                Transfer
              </button>
              <button className="btn" disabled={busy} onClick={() => setShowPad((v) => !v)}>
                Keypad
              </button>
              <button
                className={`btn${phoneState.recording ? ' danger' : ''}`}
                disabled={busy}
                onClick={() =>
                  void run(phoneState.recording ? phoneState.stopRecording : phoneState.startRecording)
                }
              >
                {phoneState.recording ? 'Stop recording' : 'Record'}
              </button>
            </div>

            {showPad && (
              <div className="dialpad" style={{ marginTop: 10 }}>
                {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map((digit) => (
                  <button key={digit} type="button" onClick={() => void run(() => phoneState.sendDtmf(digit))}>
                    {digit}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <form
            className="row"
            onSubmit={(event) => {
              event.preventDefault();
              if (!dialing.trim()) return;
              void run(async () => {
                await phoneState.dial(dialing.trim());
                setDialing('');
              });
            }}
          >
            <input
              type="tel"
              placeholder="Dial a number or extension"
              value={dialing}
              onChange={(e) => setDialing(e.target.value)}
              style={{ flex: 1 }}
            />
            <button className="btn primary" type="submit" disabled={busy || !dialing.trim()}>
              Call
            </button>
          </form>
        )}

        <div>
          <h4 style={{ marginBottom: 6 }}>
            Waiting ({phoneState.queue.waiting.length})
            {phoneState.queue.longestWaitSeconds > 0 && (
              <span className="faint"> · longest {duration(phoneState.queue.longestWaitSeconds)}</span>
            )}
          </h4>
          {phoneState.queue.waiting.length === 0 ? (
            <p className="faint" style={{ margin: 0 }}>No callers waiting.</p>
          ) : (
            <ul className="list">
              {phoneState.queue.waiting.map((waiting) => {
                const waited = since(waiting.queued_at, now);
                const overflow = waiting.overflow_after_seconds ?? 0;
                // Amber once the caller is within 30s of the queue's overflow.
                const urgent = overflow > 0 && waited > overflow - 30;
                return (
                  <li key={waiting.id}>
                    <div className="item" style={{ cursor: 'default' }}>
                      <span className="grow">
                        <span className="item-title">
                          {phone(waiting.ani)}
                          {waiting.ivr_path && <span className="badge neutral">IVR {waiting.ivr_path}</span>}
                        </span>
                        <span className="item-sub">
                          {waiting.known_caller_name ?? waiting.caller_id_name ?? 'Unknown'} ·{' '}
                          {waiting.queue_name ?? 'Unqueued'} · #{waiting.position}
                        </span>
                      </span>
                      <span className={`badge ${urgent ? 'danger' : 'warn'}`}>{duration(waited)}</span>
                      <button
                        className="btn primary sm"
                        disabled={busy || Boolean(call)}
                        title={call ? 'Finish the current call first' : 'Answer this caller'}
                        onClick={() => void run(() => phoneState.answer(waiting.id))}
                      >
                        Answer
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {phoneState.queue.active.length > 0 && (
          <div>
            <h4 style={{ marginBottom: 6 }}>On calls</h4>
            <ul className="list">
              {phoneState.queue.active.map((active) => (
                <li key={active.id}>
                  <div className="item" style={{ cursor: 'default' }}>
                    <span className="grow">
                      <span className="item-title">{phone(active.ani ?? active.dnis)}</span>
                      <span className="item-sub">
                        {active.agent_name ?? 'Unassigned'} · {titleCase(active.state)}
                      </span>
                    </span>
                    <span className="faint">{elapsed(active.answered_at, now)}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {showTransfer && call && (
        <TransferDialog
          onClose={() => setShowTransfer(false)}
          onTransfer={async (target, mode) => {
            await run(() => phoneState.transfer(target, mode));
            setShowTransfer(false);
          }}
        />
      )}
    </Card>
  );
}

function TransferDialog({
  onClose,
  onTransfer,
}: {
  onClose: () => void;
  onTransfer: (
    target: { userId?: string; extension?: string; number?: string },
    mode: 'blind' | 'attended',
  ) => Promise<void>;
}) {
  const directory = useResource<{ users: DirectoryUser[] }>('/users/directory');
  const [number, setNumber] = useState('');
  const [mode, setMode] = useState<'blind' | 'attended'>('blind');

  const reachable = (directory.data?.users ?? []).filter((user) => user.can_take_calls);

  return (
    <Modal
      title="Transfer call"
      onClose={onClose}
      footer={
        <>
          <span className="faint" style={{ marginRight: 'auto' }}>
            {mode === 'blind'
              ? 'Blind: the caller is handed over immediately.'
              : 'Attended: the caller waits on hold until you complete the transfer.'}
          </span>
          <button className="btn" onClick={onClose}>Cancel</button>
        </>
      }
    >
      <div className="stack">
        <div className="row tight">
          <button className={`chip${mode === 'blind' ? ' on' : ''}`} onClick={() => setMode('blind')}>
            Blind
          </button>
          <button className={`chip${mode === 'attended' ? ' on' : ''}`} onClick={() => setMode('attended')}>
            Attended
          </button>
        </div>

        <h4>Colleagues</h4>
        {reachable.length === 0 ? (
          <p className="faint">Nobody else is staffing a phone position.</p>
        ) : (
          <ul className="list scroll-sm">
            {reachable.map((user) => (
              <li key={user.id}>
                <button
                  className="item"
                  onClick={() =>
                    void onTransfer(
                      { userId: user.id, extension: user.sip_extension ?? undefined },
                      mode,
                    )
                  }
                >
                  <span className="grow">
                    <span className="item-title">{user.full_name}</span>
                    <span className="item-sub">
                      {user.role.replace('_', ' ')} · ext {user.sip_extension ?? '—'}
                    </span>
                  </span>
                  <span className={`badge ${user.phone_state === 'available' ? 'ok' : 'neutral'}`}>
                    {titleCase(user.phone_state)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <Field label="Or an external number">
          {(id) => (
            <div className="row">
              <input id={id} type="tel" value={number} onChange={(e) => setNumber(e.target.value)} style={{ flex: 1 }} />
              <button
                className="btn primary"
                disabled={!number.trim()}
                onClick={() => void onTransfer({ number: number.trim() }, mode)}
              >
                Transfer
              </button>
            </div>
          )}
        </Field>
      </div>
    </Modal>
  );
}
